// AGC: Lotes service — Zustand store for archive blacklist + TanStack Query for plot data
// AGC: Replaces the internal fetch/mutation logic of LotesContext
// AGC: Offline queue logic remains in LotesContext (Droidfarm-specific, not Query's concern)
// AGC: Offline-first — caches plot list in localStorage, returns cached data when offline
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, OfflineError } from './api'
import type { Plot, Field } from '../types'

// ============================================================
// AGC: Offline cache — localStorage for plot data
// ============================================================

const PLOTS_CACHE_KEY = 'droidfarm_plots_cache'

function cachePlots(plots: Plot[]): void {
  try {
    localStorage.setItem(PLOTS_CACHE_KEY, JSON.stringify({ plots, cachedAt: Date.now() }))
  } catch { /* AGC: ignore storage errors */ }
}

function getCachedPlots(): Plot[] | null {
  try {
    const raw = localStorage.getItem(PLOTS_CACHE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return parsed.plots ?? null
    }
  } catch { /* AGC: ignore parse errors */ }
  return null
}

// ============================================================
// AGC: Zustand store — archive blacklist + UI toggles
// ============================================================

interface LotesStore {
  archivedIds: string[]
  showArchived: boolean
  addArchivedId: (id: string) => void
  removeArchivedId: (id: string) => void
  setShowArchived: (show: boolean) => void
  clearArchivedIds: () => void
}

export const useLotesStore = create<LotesStore>()(
  persist(
    (set, get) => ({
      archivedIds: [],
      showArchived: false,
      addArchivedId: (id) => set((s) => ({
        archivedIds: s.archivedIds.includes(id) ? s.archivedIds : [...s.archivedIds, id],
      })),
      removeArchivedId: (id) => set((s) => ({
        archivedIds: s.archivedIds.filter((aid) => aid !== id),
      })),
      setShowArchived: (show) => set({ showArchived: show }),
      clearArchivedIds: () => set({ archivedIds: [] }),
    }),
    { name: 'droidfarm-lotes-archive' },
  ),
)

// ============================================================
// AGC: Query hooks
// ============================================================

// AGC: Fetch all plots from backend (extracted from fields response)
// AGC: Offline-first — returns localStorage cache when offline, updates cache on success
export function usePlotsQuery(enabled = true) {
  const { archivedIds } = useLotesStore()

  return useQuery({
    queryKey: ['plots'],
    queryFn: async (): Promise<Plot[]> => {
      // AGC: If offline, return cached plots immediately
      if (!navigator.onLine) {
        const cached = getCachedPlots()
        if (cached) return cached
        throw new OfflineError()
      }

      const data = await api.get<{ fields: Field[] }>('/api/v1/fields')
      const archivedSet = new Set(archivedIds)
      const allPlots: Plot[] = []

      if (data.fields) {
        for (const field of data.fields) {
          if (field.plots && Array.isArray(field.plots)) {
            for (const plot of field.plots) {
              const isInBlacklist = archivedSet.has(plot.id)
              const serverArchived = (plot as Plot & { isArchived?: boolean }).isArchived === true
              allPlots.push({
                ...plot,
                fieldId: field.id,
                isArchived: isInBlacklist || serverArchived,
              } as Plot)
            }
          }
        }
      }

      // AGC: Deduplicate by ID
      const unique = new Map(allPlots.map((p) => [p.id, p]))
      const plots = Array.from(unique.values())
      // AGC: Cache for offline use
      cachePlots(plots)
      return plots
    },
    enabled,
    staleTime: 30_000,
    retry: (count, error) => {
      if (error instanceof OfflineError) return false
      if (error instanceof ApiError && error.status === 401) return false
      return count < 2
    },
    // AGC: Seed initial data from cache so the UI renders immediately offline
    placeholderData: () => getCachedPlots() ?? undefined,
  })
}

// ============================================================
// AGC: Mutation hooks
// ============================================================

export function useAddPlotMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (plot: Plot) => {
      const result = await api.post<{ plot: Plot }>(`/api/v1/fields/${plot.fieldId}/plots`, plot)
      return result.plot || plot
    },
    onSuccess: (serverPlot, localPlot) => {
      // AGC: Replace optimistic entry with server data
      qc.setQueryData<Plot[]>(['plots'], (old) => {
        if (!old) return [serverPlot]
        return old.map((p) => (p.id === localPlot.id ? { ...serverPlot, fieldId: serverPlot.fieldId || localPlot.fieldId } : p))
      })
    },
  })
}

export function useArchivePlotMutation() {
  const qc = useQueryClient()
  const { addArchivedId } = useLotesStore()

  return useMutation({
    mutationFn: async ({ plotId, fieldId }: { plotId: string; fieldId: string }) => {
      addArchivedId(plotId)
      await api.patch<void>(`/api/v1/fields/${fieldId}/plots/${plotId}/archive`, {})
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plots'] })
    },
    onError: (_, { plotId }) => {
      // AGC: Revert on failure
      useLotesStore.getState().removeArchivedId(plotId)
    },
  })
}

export function useUnarchivePlotMutation() {
  const qc = useQueryClient()
  const { removeArchivedId } = useLotesStore()

  return useMutation({
    mutationFn: async ({ plotId, fieldId }: { plotId: string; fieldId: string }) => {
      removeArchivedId(plotId)
      await api.patch<void>(`/api/v1/fields/${fieldId}/plots/${plotId}/unarchive`, {})
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plots'] })
    },
    onError: (_, { plotId }) => {
      useLotesStore.getState().addArchivedId(plotId)
    },
  })
}
